type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export type HttpClientOptions = {
  ignoreErrorHook?: Boolean;
  omitErrorMessage?: Boolean;
  errorMessage?: string;
  headers?: Headers;
  httpsAgent?: any;
};

type HttpClientArgs = {
  endpoint: string;
  method?: HttpMethod;
  headers?: Headers;
  body?: any;
  requestOptions?: HttpClientOptions;
  params?: any;
};

interface State {
  unresolvedRefresh: Promise<any> | null;
}
export interface HookData extends HttpClientArgs {
  baseUrl: string;
  controller: AbortController;
  state: State;
}

export interface ErrorHookData extends HookData {
  error: unknown;
  errorMessage?: string;
  omitErrorMessage?: boolean;
}

type Hooks = {
  onBeforeRequest?: (hookData: HookData) => Promise<void> | void;
  onRequestError?: (hookData: ErrorHookData) => Promise<void> | void;
};

export type HttpResponse<T, K> = {
  data?: T;
  error?: K;
};

export type HttpClient = <T, K = unknown>(args: HttpClientArgs) => Promise<HttpResponse<T, K>>;

const state = {
  unresolvedRefresh: null,
};

export const createHttpClient = (
  baseUrl: string,
  controller: AbortController,
  hooks: Hooks,
  commonOptions?: HttpClientOptions,
): HttpClient => {
  return async ({ endpoint, method = 'GET', headers, body, requestOptions = {}, ...rest }: HttpClientArgs) => {
    requestOptions = { ...commonOptions, ...requestOptions };
    const hookData = {
      baseUrl,
      controller,
      requestOptions,
      method,
      headers,
      endpoint,
      state,
    };
    try {
      await hooks.onBeforeRequest?.(hookData);
      const options = {
        headers: { ...headers, ...requestOptions.headers },
        method,
        body,
        signal: controller.signal,
        ...rest,
      };
      if (requestOptions.httpsAgent) {
        options.dispatcher = requestOptions.httpsAgent;
      }
      const data = (await $fetch(baseUrl + endpoint, options)) as any;
      return { data };
    } catch (error: any) {
      if (!requestOptions.ignoreErrorHook) {
        await hooks.onRequestError?.({ ...hookData, error });
      }
      return { error };
    }
  };
};
